/* eslint-disable react-hooks/exhaustive-deps */
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { errorToast, successToast } from "../../components/Toast";
import {
  getProductByIdApi,
  updateProductApi,
  getParentProductNameListApi,
} from "../../services/_product";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [updateLoading, setUpdateLoading] = useState(false);
  const [isChildProduct, setIsChildProduct] = useState(false);
  const [parentProducts, setParentProducts] = useState<any[]>([]);

  const createProductInitialValues = {
    name: "",
    variety: "",
    description: "",
    hsn_no: "",
    status: "",
    parent_product_id: "",
  };

  const [initialValues, setInitialValues] = useState(
    createProductInitialValues
  );

  useEffect(() => {
    if (!id) {
      navigate("/product-list");
    } else {
      getProductDetails();
    }
  }, []);

  const getProductDetails = async () => {
    const result: any = await getProductByIdApi(id as string);

    if (result.isSuccess) {
      const data = result.data;
      const isChild = !!data.parent_product_id;

      setIsChildProduct(isChild);

      if (isChild) {
        loadParentProducts();
      }

      setInitialValues({
        name: data.name || "",
        variety: data.variety || "",
        description: data.description || "",
        hsn_no: data.hsn_no || "",
        status: data.status?.toString(),
        parent_product_id: data.parent_product_id || "",
      });
    }
  };

  const loadParentProducts = async () => {
    const result: any = await getParentProductNameListApi();
    if (result.isSuccess) {
      setParentProducts(result.data);
    }
  };

  useEffect(() => {
    if (
      isChildProduct &&
      parentProducts.length > 0 &&
      initialValues.parent_product_id
    ) {
      const parent = parentProducts.find(
        (p) => p.id === initialValues.parent_product_id
      );
      if (parent && parent.name !== initialValues.name) {
        setInitialValues((prev) => ({
          ...prev,
          name: parent.name, // updated parent name
        }));
      }
    }
  }, [parentProducts]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    variety: isChildProduct
      ? Yup.string().required("Variety is required")
      : Yup.string(),
    status: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values: any, { resetForm }: any) => {
    setUpdateLoading(true);

    const payload: any = {
      name: values.name,
      description: values.description,
      hsn_no: values.hsn_no,
      status: values.status === "true",
    };

    if (isChildProduct) {
      payload.variety = values.variety;
      payload.parent_product_id = values.parent_product_id;
    }

    const result: any = await updateProductApi(id as string, payload);

    if (result.isSuccess) {
      successToast(result.message);
      resetForm();
      navigate("/product-list");
    } else {
      errorToast(result.message);
    }

    setUpdateLoading(false);
  };

  return (
    <>
      <div className="card">
        <div className="card-header pb-0">
          <h5>Edit Product</h5>
        </div>

        <div className="card-body">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, setFieldValue }) => (
              <Form className="dt_adv_search">
                <div className="row g-3">
                  {/* NAME FIELD */}
                  <div className="col-12 col-sm-6 col-lg-4">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>

                    {!isChildProduct ? (
                      <Field
                        name="name"
                        className="form-control"
                        placeholder="Enter name"
                      />
                    ) : (
                      <select
                        className="form-select"
                        value={values.parent_product_id}
                        onChange={(e) => {
                          const selected = parentProducts.find(
                            (p) => p.id === e.target.value
                          );

                          setFieldValue(
                            "parent_product_id",
                            selected?.id || ""
                          );
                          setFieldValue("name", selected?.name || "");
                        }}
                      >
                        <option value="">Select Parent Product</option>
                        {parentProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    )}

                    <ErrorMessage
                      name="name"
                      className="text-danger"
                      component="div"
                    />
                  </div>

                  {/* VARIETY - ONLY CHILD */}
                  {isChildProduct && (
                    <div className="col-12 col-sm-6 col-lg-4">
                      <label className="form-label">
                        Variety <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="variety"
                        className="form-control"
                        placeholder="Enter variety"
                      />
                      <ErrorMessage
                        name="variety"
                        className="text-danger"
                        component="div"
                      />
                    </div>
                  )}

                  <div className="col-12 col-sm-6 col-lg-4">
                    <label className="form-label">HSN No</label>
                    <Field
                      name="hsn_no"
                      className="form-control"
                      placeholder="Enter HSN no"
                    />
                  </div>

                  <div className="col-12 col-sm-6 col-lg-4">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      name="status"
                      className="form-select"
                      value={values.status}
                      onChange={handleChange}
                    >
                      <option value="">Select Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                    <ErrorMessage
                      name="status"
                      className="text-danger"
                      component="div"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <Field
                      as="textarea"
                      rows={2}
                      name="description"
                      className="form-control"
                    />
                  </div>

                  <div className="col-12">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={updateLoading}
                    >
                      {updateLoading && (
                        <span className="spinner-border spinner-border-sm me-2" />
                      )}
                      Submit
                    </button>

                    <Link
                      to="/product-list"
                      className="btn btn-label-github ms-5"
                    >
                      Back
                    </Link>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default EditProduct;
